import * as React from 'react';

/**
 * Reference-counted scroll lock.
 *
 * Multiple overlays (Modal, AlertDialog, Drawer) can call `useScrollLock(true)`
 * concurrently. The lock is applied when the first consumer activates and
 * released only when the last consumer deactivates. This prevents the bug where
 * closing an inner overlay restores scrolling while an outer overlay is still open.
 */

let lockCount = 0;
let savedOverflow = '';
let savedPaddingRight = '';

function lock(): void {
  lockCount += 1;
  if (lockCount > 1) return;

  if (typeof document === 'undefined') return;

  const html = document.documentElement;
  const body = document.body;

  savedOverflow = body.style.overflow;
  savedPaddingRight = body.style.paddingRight;

  const scrollbarWidth = window.innerWidth - html.clientWidth;
  body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }
}

function unlock(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;

  if (typeof document === 'undefined') return;

  const body = document.body;
  body.style.overflow = savedOverflow;
  body.style.paddingRight = savedPaddingRight;
}

export function useScrollLock(active: boolean): void {
  React.useEffect(() => {
    if (!active) return undefined;

    lock();
    return () => unlock();
  }, [active]);
}
